import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Duration } from 'dayjs/plugin/duration';
import { startWith, takeUntil } from 'rxjs';

import { DefaultIcon, SDuration } from '@summit/core';
import { NumberFormatter, SummitIdObject } from '@summit/models';

import { Button } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Popover } from 'primeng/popover';
import { SummitControl } from '../../models';
import { SFormErrorInputComponent } from '../s-form-error-input';

@Component({
  selector: 's-form-input-duration',
  templateUrl: './s-form-input-duration.component.html',
  styleUrls: ['./s-form-input-duration.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SFormErrorInputComponent,
    InputGroupAddon,
    InputGroup,
    InputText,
    FloatLabel,
    Popover,
    InputNumber,
    Button
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputDurationComponent<T extends SummitIdObject> extends SummitControl<T> implements OnInit {
  @Input() stepMinute = 15;
  inputValue?: string;
  defaultIcon = DefaultIcon;
  overlayHourValue: number;
  overlayMinValue: number;

  private get control() {
    const controlName = this.fieldName?.toString();
    return controlName ? this.form?.controls?.[controlName] : undefined;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.form.controls[this.fieldName ?? '']?.valueChanges
      .pipe(startWith(this.form.controls[this.fieldName ?? '']?.value), takeUntil(this.disposing$))
      .subscribe((value) => {
        //console.log(`${this.fieldName}: value changed`, value);
        if (SDuration.isDuration(value)) {
          //console.log(`${this.fieldName}: updating control with local values`, value.hours());
          //setTimeout(() => this.dateControl.writeValue(new Date(1970, 0, 1, value.hours(), value.minutes())));
          //console.log(`value ${value.days()}:${value.hours()}:${value.minutes()}`);
          this.overlayHourValue = (value.hours() ?? 0) + (value.days() ?? 0) * 24;
          //console.log(`overlayHourValue ${this.overlayHourValue}`);
          this.overlayMinValue = value.minutes() ?? 0;
          this.inputValue = `${this.overlayHourValue}:${NumberFormatter(this.overlayMinValue, 2)}`;
          //console.log(`${this.fieldName}: updating control with value: ${this.inputValue}`);
          this.cd.detectChanges();
        } else if (value instanceof Date) {
          //console.log(`${this.fieldName}: updating value with Dayjs`);
          const hours = value.getHours() + value.getDate() * 24;
          const minutes = value.getMinutes();
          if (hours > 0 || minutes > 0) {
            //console.log(`Publishing new value ${hours}:${minutes}`);
            this.form.controls[this.fieldName ?? '']?.setValue(
              SDuration.new({ hours: hours, minutes: minutes }, false)
            );
          }
        }
      });
  }

  /// <summary>
  /// This is a hack to get the input to work for typing
  /// Needs to support:
  /* 
  "1" as hours
  "56", "128" as minutes
  "1:56", "1,56", "1 56" as hours and minutes
  "1.2" as hours and fraction of an hour
  */
  /// </summary>
  onBlur(_) {
    const rawString = this.inputValue;
    //console.log('onBlur', this.inputValue, _);
    if ((rawString?.length ?? 0) > 0) {
      //console.log('raw string: ', rawString);
      this.form.controls[this.fieldName ?? '']?.setValue(SDuration.new(rawString));
    }
  }

  getObject(): Duration | undefined {
    const value = this.form.controls[this.fieldName ?? '']?.value;
    if (value != null && value != undefined) {
      //console.log('testing for duration', value);
      if (SDuration.isDuration(value)) {
        return value;
      }
      return SDuration.new(value);
    }
    return undefined;
  }

  decreaseMin() {
    const value = this.form.controls[this.fieldName ?? '']?.value;
    if (SDuration.isDuration(value)) {
      this.form.controls[this.fieldName ?? '']?.setValue(value.subtract(1, 'minutes'));
    }
  }

  verifyInitialValue() {
    this.overlayHourValue ??= 0;
    this.overlayMinValue ??= 0;
  }

  increaseMin() {
    const value = this.form.controls[this.fieldName ?? '']?.value;
    if (SDuration.isDuration(value)) {
      this.form.controls[this.fieldName ?? '']?.setValue(value.add(1, 'minutes'));
    }
  }
  decreaseHour() {
    const value = this.form.controls[this.fieldName ?? '']?.value;
    if (SDuration.isDuration(value)) {
      this.form.controls[this.fieldName ?? '']?.setValue(value.subtract(1, 'hour'));
    }
  }

  increaseHour() {
    const value = this.form.controls[this.fieldName ?? '']?.value;
    if (SDuration.isDuration(value)) {
      this.form.controls[this.fieldName ?? '']?.setValue(value.add(1, 'hour'));
    }
  }

  updateFromPartials() {
    //console.log(`updateFromPartials ${this.overlayHourValue}:${this.overlayMinValue}`);
    if (this.overlayHourValue < 0) this.overlayHourValue = 0;
    if (this.overlayMinValue < 0) this.overlayMinValue = 59;
    else if (this.overlayMinValue > 59) this.overlayMinValue = 0;
    const newValue = SDuration.new({ hours: this.overlayHourValue, minutes: this.overlayMinValue });
    //console.log(`updateFromPartials ${newValue.format()}`);
    this.form.controls[this.fieldName ?? '']?.setValue(newValue);
  }

  clear($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.form.controls[this.fieldName ?? '']?.setValue(undefined);
    this.inputValue = undefined;
  }
}
